import API from "../Utils/API";
import constants from "../Utils/constants";

// Forbidden actions surface as 500, not 403 (the app has no rescue_from for
// Pundit::NotAuthorizedError), so admin-only calls are gated on role in the UI
// rather than by inspecting the status code here.
const firstError = (data) =>
  (Array.isArray(data?.errors) ? data.errors[0] : data?.errors) || null;

export const getAllPreLeads = (url, successCallback) => (dispatch) => {
  dispatch({ type: constants.GET_PRE_LEADS_REQUEST });
  API.get(url)
    .then((response) => {
      dispatch({
        type: constants.GET_PRE_LEADS_SUCCESS,
        payload: response?.data,
      });
      successCallback && successCallback(response?.data);
    })
    .catch((error) => {
      dispatch({
        type: constants.GET_PRE_LEADS_FAILURE,
        payload: error.response?.data,
      });
    });
};

export const getPreLeadById =
  (id, successCallback, failureCallback) => (dispatch) => {
    dispatch({ type: constants.GET_PRE_LEAD_BY_ID_REQUEST });
    API.get(`/pre_leads/${id}`)
      .then((response) => {
        dispatch({
          type: constants.GET_PRE_LEAD_BY_ID_SUCCESS,
          payload: response?.data,
        });
        successCallback && successCallback(response?.data);
      })
      .catch((error) => {
        dispatch({
          type: constants.GET_PRE_LEAD_BY_ID_FAILURE,
          payload: error.response?.data,
        });
        failureCallback && failureCallback(error.response?.data);
      });
  };

/**
 * Admin hands a pre-lead to an employee. Also used to reassign one that is
 * already held. Admin only - an employee calling this gets a 500.
 */
export const assignPreLead =
  (id, userId, successCallback, failureCallback) => (dispatch) => {
    dispatch({ type: constants.ASSIGN_PRE_LEAD_REQUEST });
    API.patch(`/pre_leads/${id}/assign`, { user_id: userId })
      .then((response) => {
        dispatch({
          type: constants.ASSIGN_PRE_LEAD_SUCCESS,
          payload: response?.data,
        });
        successCallback && successCallback(response?.data);
      })
      .catch((error) => {
        dispatch({
          type: constants.ASSIGN_PRE_LEAD_FAILURE,
          payload: error.response?.data,
        });
        failureCallback && failureCallback(firstError(error.response?.data));
      });
  };

/** The current holder (or an admin) passes a pre-lead on to someone else. */
export const transferPreLead =
  (id, userId, successCallback, failureCallback) => (dispatch) => {
    dispatch({ type: constants.TRANSFER_PRE_LEAD_REQUEST });
    API.patch(`/pre_leads/${id}/transfer`, { user_id: userId })
      .then((response) => {
        dispatch({
          type: constants.TRANSFER_PRE_LEAD_SUCCESS,
          payload: response?.data,
        });
        successCallback && successCallback(response?.data);
      })
      .catch((error) => {
        dispatch({
          type: constants.TRANSFER_PRE_LEAD_FAILURE,
          payload: error.response?.data,
        });
        failureCallback && failureCallback(firstError(error.response?.data));
      });
  };

/**
 * Sets, updates or clears the next follow-up date. `followUpAt` must be sent
 * even when clearing - pass null. Omitting the key entirely is a 422 server
 * side, deliberately, so a buggy form cannot wipe a date by accident.
 */
export const setPreLeadFollowUp =
  (id, followUpAt, successCallback, failureCallback) => (dispatch) => {
    dispatch({ type: constants.FOLLOW_UP_PRE_LEAD_REQUEST });
    API.patch(`/pre_leads/${id}/follow_up`, { follow_up_at: followUpAt })
      .then((response) => {
        dispatch({
          type: constants.FOLLOW_UP_PRE_LEAD_SUCCESS,
          payload: response?.data,
        });
        successCallback && successCallback(response?.data);
      })
      .catch((error) => {
        dispatch({
          type: constants.FOLLOW_UP_PRE_LEAD_FAILURE,
          payload: error.response?.data,
        });
        failureCallback && failureCallback(firstError(error.response?.data));
      });
  };

/**
 * "We worked it, it's closed." Distinct from deletePreLead below - a dismissed
 * pre-lead stays visible and can be reopened.
 */
export const dismissPreLead =
  (id, dismissalReason, successCallback, failureCallback) => (dispatch) => {
    dispatch({ type: constants.DISMISS_PRE_LEAD_REQUEST });
    API.patch(`/pre_leads/${id}/dismiss`, { dismissal_reason: dismissalReason })
      .then((response) => {
        dispatch({
          type: constants.DISMISS_PRE_LEAD_SUCCESS,
          payload: response?.data,
        });
        successCallback && successCallback(response?.data);
      })
      .catch((error) => {
        dispatch({
          type: constants.DISMISS_PRE_LEAD_FAILURE,
          payload: error.response?.data,
        });
        failureCallback && failureCallback(firstError(error.response?.data));
      });
  };

export const reopenPreLead =
  (id, successCallback, failureCallback) => (dispatch) => {
    dispatch({ type: constants.REOPEN_PRE_LEAD_REQUEST });
    API.patch(`/pre_leads/${id}/reopen`)
      .then((response) => {
        dispatch({
          type: constants.REOPEN_PRE_LEAD_SUCCESS,
          payload: response?.data,
        });
        successCallback && successCallback(response?.data);
      })
      .catch((error) => {
        dispatch({
          type: constants.REOPEN_PRE_LEAD_FAILURE,
          payload: error.response?.data,
        });
        failureCallback && failureCallback(firstError(error.response?.data));
      });
  };

export const positivePreLead =
  (id, successCallback, failureCallback) => (dispatch) => {
    dispatch({ type: constants.POSITIVE_PRE_LEAD_REQUEST });
    API.patch(`/pre_leads/${id}/positive`)
      .then((response) => {
        dispatch({
          type: constants.POSITIVE_PRE_LEAD_SUCCESS,
          payload: response?.data,
        });
        successCallback && successCallback(response?.data);
      })
      .catch((error) => {
        dispatch({
          type: constants.POSITIVE_PRE_LEAD_FAILURE,
          payload: error.response?.data,
        });
        failureCallback && failureCallback(firstError(error.response?.data));
      });
  };

/**
 * Admin-only soft delete - "this was spam, hide it". Not the same as dismiss,
 * and there is no undelete endpoint.
 */
export const deletePreLead =
  (id, successCallback, failureCallback) => (dispatch) => {
    dispatch({ type: constants.DELETE_PRE_LEAD_REQUEST });
    API.delete(`/pre_leads/${id}`)
      .then((response) => {
        dispatch({
          type: constants.DELETE_PRE_LEAD_SUCCESS,
          payload: response?.data,
        });
        successCallback && successCallback();
      })
      .catch((error) => {
        dispatch({
          type: constants.DELETE_PRE_LEAD_FAILURE,
          payload: error.response?.data,
        });
        failureCallback && failureCallback(firstError(error.response?.data));
      });
  };

/************************************ NOTES ************************************/

/** Oldest-first timeline, mixing employee notes with system audit entries. */
export const getPreLeadNotes = (preLeadId, successCallback) => (dispatch) => {
  dispatch({ type: constants.GET_PRE_LEAD_NOTES_REQUEST });
  API.get(`/pre_leads/${preLeadId}/notes`)
    .then((response) => {
      dispatch({
        type: constants.GET_PRE_LEAD_NOTES_SUCCESS,
        payload: response?.data,
      });
      successCallback && successCallback(response?.data);
    })
    .catch((error) => {
      dispatch({
        type: constants.GET_PRE_LEAD_NOTES_FAILURE,
        payload: error.response?.data,
      });
    });
};

export const createPreLeadNote =
  (preLeadId, note, successCallback, failureCallback) => (dispatch) => {
    dispatch({ type: constants.CREATE_PRE_LEAD_NOTE_REQUEST });
    // The pre_lead_note wrapper matches the existing lead notes endpoint.
    API.post(`/pre_leads/${preLeadId}/notes`, { pre_lead_note: note })
      .then((response) => {
        dispatch({
          type: constants.CREATE_PRE_LEAD_NOTE_SUCCESS,
          payload: response?.data,
        });
        successCallback && successCallback(response?.data);
      })
      .catch((error) => {
        dispatch({
          type: constants.CREATE_PRE_LEAD_NOTE_FAILURE,
          payload: error.response?.data,
        });
        failureCallback && failureCallback(firstError(error.response?.data));
      });
  };

export const updatePreLeadNote =
  (preLeadId, noteId, note, successCallback, failureCallback) => (dispatch) => {
    dispatch({ type: constants.UPDATE_PRE_LEAD_NOTE_REQUEST });
    API.patch(`/pre_leads/${preLeadId}/notes/${noteId}`, {
      pre_lead_note: note,
    })
      .then((response) => {
        dispatch({
          type: constants.UPDATE_PRE_LEAD_NOTE_SUCCESS,
          payload: response?.data,
        });
        successCallback && successCallback(response?.data);
      })
      .catch((error) => {
        dispatch({
          type: constants.UPDATE_PRE_LEAD_NOTE_FAILURE,
          payload: error.response?.data,
        });
        failureCallback && failureCallback(firstError(error.response?.data));
      });
  };

export const deletePreLeadNote =
  (preLeadId, noteId, successCallback, failureCallback) => (dispatch) => {
    dispatch({ type: constants.DELETE_PRE_LEAD_NOTE_REQUEST });
    API.delete(`/pre_leads/${preLeadId}/notes/${noteId}`)
      .then(() => {
        dispatch({ type: constants.DELETE_PRE_LEAD_NOTE_SUCCESS });
        successCallback && successCallback();
      })
      .catch((error) => {
        dispatch({
          type: constants.DELETE_PRE_LEAD_NOTE_FAILURE,
          payload: error.response?.data,
        });
        failureCallback && failureCallback(firstError(error.response?.data));
      });
  };

/******************************* CLIENT MATCHING *******************************/

/** Shows what would be created or matched. Writes nothing. */
export const getPreLeadClientPreview =
  (preLeadId, successCallback, failureCallback) => (dispatch) => {
    dispatch({ type: constants.PRE_LEAD_CLIENT_PREVIEW_REQUEST });
    API.get(`/pre_leads/${preLeadId}/client_preview`)
      .then((response) => {
        dispatch({
          type: constants.PRE_LEAD_CLIENT_PREVIEW_SUCCESS,
          payload: response?.data,
        });
        successCallback && successCallback(response?.data);
      })
      .catch((error) => {
        dispatch({
          type: constants.PRE_LEAD_CLIENT_PREVIEW_FAILURE,
          payload: error.response?.data,
        });
        failureCallback && failureCallback(firstError(error.response?.data));
      });
  };

/**
 * Finds an existing client by phone or creates one, then links it to the
 * pre-lead. `enrich` fills blank fields on an existing client; it never
 * overwrites a value that is already set.
 */
export const createPreLeadClient =
  (preLeadId, { client, enrich } = {}, successCallback, failureCallback) =>
  (dispatch) => {
    dispatch({ type: constants.CREATE_PRE_LEAD_CLIENT_REQUEST });
    const body = {};
    if (client && Object.keys(client).length > 0) body.client = client;
    if (enrich) body.enrich = true;

    API.post(`/pre_leads/${preLeadId}/client`, body)
      .then((response) => {
        dispatch({
          type: constants.CREATE_PRE_LEAD_CLIENT_SUCCESS,
          payload: response?.data,
        });
        successCallback && successCallback(response?.data);
      })
      .catch((error) => {
        dispatch({
          type: constants.CREATE_PRE_LEAD_CLIENT_FAILURE,
          payload: error.response?.data,
        });
        failureCallback && failureCallback(firstError(error.response?.data));
      });
  };
